/**
 * Utility functions for formatting display values
 */

/**
 * Format tier value for display
 * @param {string} tier - The tier value (tier1, tier2, tier3, rural)
 * @returns {string} - Formatted tier name (Tier 1, Tier 2, Tier 3, Rural)
 */
export const formatTierName = (tier) => {
  if (!tier) return '';

  const tierMap = {
    tier1: 'Tier 1',
    tier2: 'Tier 2',
    tier3: 'Tier 3',
    rural: 'Rural',
    // Legacy support
    premium: 'Premium',
    standard: 'Standard',
    basic: 'Basic',
  };

  return tierMap[tier] || tier;
};

/**
 * Format media type for display
 * @param {string} mediaType - The media type value (metro_train, bus, auto, cab)
 * @returns {string} - Formatted media type (Metro Train, Bus, Auto, Cab)
 */
export const formatMediaType = (mediaType) => {
  if (!mediaType) return '';

  const mediaTypeMap = {
    metro_train: 'Metro Train',
    bus: 'Bus',
    auto: 'Auto',
    cab: 'Cab',
    // Also handle if passed as snake_case
    train: 'Train',
    metro: 'Metro',
    taxi: 'Taxi',
  };

  return mediaTypeMap[mediaType] || mediaType;
};

/**
 * Format audience value for display
 * @param {string|string[]} audience - The audience value(s)
 * @returns {string} - Formatted audience name
 */
export const formatAudience = (audience) => {
  if (!audience) return '';

  const audienceMap = {
    general: 'General',
    youth: 'Youth',
    adults: 'Adults',
    seniors: 'Seniors',
    business: 'Business',
  };

  if (Array.isArray(audience)) {
    return audience
      .filter(a => a != null) // Filter out null/undefined values
      .map(a => audienceMap[a] || a)
      .join(', ');
  }

  return audienceMap[audience] || audience;
};

/**
 * Format time slot for display
 * @param {string} timeSlot - The time slot value (morning, afternoon, evening, night)
 * @returns {string} - Formatted time slot
 */
export const formatTimeSlot = (timeSlot) => {
  if (!timeSlot) return '';

  const timeSlotMap = {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
    night: 'Night',
  };

  return timeSlotMap[timeSlot] || timeSlot;
};

/**
 * Format any enum field value to display format
 * @param {string|string[]} value - The value to format
 * @returns {string} - Formatted value
 */
export const formatFieldValue = (value) => {
  if (!value) return '';

  // Handle arrays by formatting each value
  if (Array.isArray(value)) {
    return value
      .filter(v => v != null) // Filter out null/undefined values
      .map(v => formatFieldValue(v))
      .join(', ');
  }

  // Ensure value is a string
  if (typeof value !== 'string') return String(value);

  // If it's already formatted (contains uppercase), return as is
  if (/[A-Z]/.test(value)) return value;

  // Convert snake_case to Title Case
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Calculate radio ad pricing
 * @param {number} pricePerSecond - Price per second
 * @param {number} seconds - Number of seconds
 * @param {number} slotsPerDay - Slots per day
 * @param {number} days - Number of days
 * @returns {number} - Total price
 */
export const calculateRadioPrice = (pricePerSecond, seconds, slotsPerDay, days) => {
  return (seconds * pricePerSecond) * slotsPerDay * days;
};

/**
 * Format currency for display
 * @param {number} amount - The amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};
